import React from "react";
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Grid,
  Image,
} from "@chakra-ui/react";
import ServiceDetailsHeader from "../../components/Common/ImageHeader";
import ExploreSection from "../../components/ProductDetails/ExploreSection";
import ServiceCard from "../../components/Common/ServiceCard";
import BenefitCard from "../../components/Common/BenefitCard";

const ETLDataWarehousePage = () => {
  // ETL & Data Warehouse Services Data
  const etlServices = [
    {
      id: 1,
      title: "Custom ETL Development",
      description:
        "Design and implement tailored ETL pipelines that automate data extraction, transformation, and loading, reducing manual effort and errors.",
      icon: "/svg/Custom_ETL_Development.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 2,
      title: "Data Warehouse Architecture",
      description:
        "Build scalable, high-performance data warehouses optimized for your reporting and analytics needs, supporting cloud and on-premise deployments.",
      icon: "/svg/Data_Warehouse_Architecture.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 3,
      title: "Data Integration",
      description:
        "Seamlessly unify data from ERP, CRM, e-Governance platforms, IoT devices, and external datasets into a single source of truth.",
      icon: "/svg/Data_Integration.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 4,
      title: "Data Governance & Security",
      description:
        "Implement role-based access, encryption, and audit trails to protect sensitive information and ensure compliance.",
      icon: "/svg/Data_Governance.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 5,
      title: "Real-time & Batch Processing",
      description:
        "Support both real-time streaming data and traditional batch processing to meet diverse operational requirements.",
      icon: "/svg/RealTime_Batch_Processing.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 6,
      title: "Analytics Enablement",
      description:
        "Prepare data for business intelligence, machine learning, and AI-driven insights that empower strategic planning.",
      icon: "/svg/Analytics_Enablement.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
  ];

  // Benefits Data
  const clientBenefits = [
    {
      id: 1,
      title: "Enhanced Decision Making",
      description:
        "Access trusted, comprehensive data that supports evidence-based policymaking and business strategies.",
    },
    {
      id: 2,
      title: "Operational Efficiency",
      description:
        "Automate complex data workflows, minimizing delays and reducing overhead.",
    },
    {
      id: 3,
      title: "Cross-Agency Collaboration",
      description:
        "Build flexible data systems that scale with your digital transformation goals.",
    },
    {
      id: 4,
      title: "Future-Ready Infrastructure",
      description:
        "Reduce operational costs through automated data processes and efficient resource utilization.",
    },
    {
      id: 5,
      title: "Citizen-Centric Services",
      description:
        "Leverage analytics to design smarter public services and improve citizen engagement.",
    },
  ];

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>ETL & Data Warehouse Solutions | Data-Driven Governance & Enterprise in Bangladesh</title>
        <meta 
          name="description" 
          content="Business Automation Ltd. offers advanced ETL and Data Warehouse solutions in Bangladesh to unify data, improve analytics, and empower smarter decision-making for government and enterprise clients." 
        />
        <meta 
          name="keywords" 
          content="ETL Bangladesh, data warehouse solutions Bangladesh, government data integration, business intelligence Bangladesh, data analytics platform Bangladesh, scalable data storage, data governance Bangladesh, real-time data processing, AI-ready data warehouse, digital transformation data Bangladesh" 
        />
        <link rel="canonical" href="https://www.ba-systems.com/services/etl-data-warehouse" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content="https://ba-systems.com/services/etl-data-warehouse" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="ETL & Data Warehouse Solutions | Data-Driven Governance & Enterprise in Bangladesh" />
        <meta property="og:description" content="Business Automation Ltd. offers advanced ETL and Data Warehouse solutions in Bangladesh to unify data, improve analytics, and empower smarter decision-making for government and enterprise clients." />
        <meta property="og:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="ba-systems.com" />
        <meta property="twitter:url" content="https://ba-systems.com/services/etl-data-warehouse" />
        <meta name="twitter:title" content="ETL & Data Warehouse Solutions | Data-Driven Governance & Enterprise in Bangladesh" />
        <meta name="twitter:description" content="Business Automation Ltd. offers advanced ETL and Data Warehouse solutions in Bangladesh to unify data, improve analytics, and empower smarter decision-making for government and enterprise clients." />
        <meta name="twitter:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />
      </Helmet>
      
      <ServiceDetailsHeader
        title="ETL & Data Warehouse Solutions"
        subtitle="Advanced Data Processing & Analytics Infrastructure"
        backgroundImage="/png/bg_etl.png"
        breadcrumbItems={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/our-services" },
          { label: "ETL & Data Warehouse Solutions", href: "#", isCurrentPage: true },
        ]}
      />

      <Box
        as="section"
        py={{ base: 4, md: 6 }}
        sx={{
          backgroundImage: "url('/png/background.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: "100vh",
        }}
      >
        <Container maxW="7xl">
          <VStack align="start">
            <Text textAlign="justify">
              At Business Automation Limited, we help organizations in Bangladesh
              harness the full potential of their data through advanced ETL
              (Extract, Transform, Load) and Data Warehousing solutions. Our
              platforms consolidate data from multiple sources into centralized
              repositories, enabling efficient analysis, reporting, and
              strategic decision-making aligned with the Digital Bangladesh
              vision.
            </Text>
            <Heading
              pt={8}
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="bold"
              color="gray.800"
            >
              Why ETL & Data{" "}
              <Text as="span" color="#000B8C">
                Warehousing Are Critical{" "}
              </Text>
            </Heading>
            <Box w="full" display="flex" justifyContent="center" py={8}>
              <Box
                as="img"
                src="/png/etl.png"
                alt="AI-driven cybersecurity capabilities"
                maxW="100%"
                height="auto"
                borderRadius="lg"
                loading="lazy"
                draggable="false"
              />
            </Box>

            {/* Our ETL & Data Warehouse Services Section */}
            <Box w="full" py={8}>
              <Heading
                size="xl"
                textAlign="center"
                mb={8}
                color="gray.800"
                fontWeight="bold"
              >
                Our ETL & Data{" "}
                <Text as="span" color="#000B8C">
                  Warehouse Services
                </Text>{" "}
              </Heading>

              {/* First Row: 3 Cards */}
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                gap={6}
                mb={6}
              >
                {etlServices.slice(0, 3).map((service) => (
                  <ServiceCard key={service.id} service={service} />
                ))}
              </Grid>

              {/* Second Row: 2 Cards */}
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                gap={6}
                mb={6}
              >
                {etlServices.slice(3, 6).map((service) => (
                  <ServiceCard key={service.id} service={service} />
                ))}
              </Grid>
            </Box>

            {/* Benefits for Our Clients Section */}
            <Box w="full" py={8}>
              <Container maxW="7xl">
                <Heading
                  size="xl"
                  textAlign="left"
                  mb={8}
                  color="gray.800"
                  fontWeight="bold"
                >
                  Benefits for{" "}
                  <Text as="span" color="#000B8C">
                    Your Organization
                  </Text>
                </Heading>

                {/* Benefits Grid - 2 columns layout */}
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={6}
                  maxW="7xl"
                  mx="auto"
                  justifyContent="center"
                  alignItems="stretch"
                >
                  {clientBenefits.map((benefit) => (
                    <Box
                      key={benefit.id}
                      w="100%"
                      maxW="500px"
                      minW={{ base: "100%", md: "400px" }}
                      flex={{ base: "1 1 100%", md: "1 1 calc(50% - 12px)" }}
                    >
                      <BenefitCard benefit={benefit} />
                    </Box>
                  ))}
                </Box>
              </Container>
            </Box>

            {/* Trusted Data Solutions Partner Section */}
            <Box w="full" py={12}>
              <Container maxW="7xl">
                <Grid
                  templateColumns={{ base: "1fr", lg: "2fr 1fr" }}
                  gap={12}
                  alignItems="center"
                >
                  {/* Left Content */}
                  <VStack align="start" spacing={6}>
                    <Heading
                      fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                      fontWeight="bold"
                      color="gray.800"
                      lineHeight="shorter"
                    >
                      <Text as="span" color="#000B8C">
                        Trusted Data Partner for
                      </Text>
                      <br></br> Bangladesh’s Digital Future
                    </Heading>

                    <Text
                      fontSize={{ base: "md", md: "lg" }}
                      color="gray.600"
                      align={"justify"}
                      lineHeight="1.2"
                    >
                      With over 25 years of experience, Business Automation Limited
                      is a leading provider of ETL and data warehousing
                      solutions for government and enterprises in Bangladesh.
                      Our expert teams combine local knowledge with global best
                      practices to deliver data platforms that power smarter
                      governance and business excellence.
                    </Text>
                  </VStack>

                  {/* Right Image */}
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Image
                      src="/svg/hand_shaking.svg"
                      alt="Data Analytics Illustration"
                      maxW="80%"
                      h="auto"
                      objectFit="contain"
                    />
                  </Box>
                </Grid>
              </Container>
            </Box>
          </VStack>
          <ExploreSection />
        </Container>
      </Box>
    </>
  );
};

export default ETLDataWarehousePage;
